/**
 * Franchisee registration business types — aligned with Business Mitra category_service.dart.
 * Grouped as Services vs Products for the registration dropdown.
 */

export const BUSINESS_TYPE_GROUPS = {
  services: 'Services',
  products: 'Products',
};

/** Top-level registration choice (filters category dropdown). */
export const BUSINESS_KIND_OPTIONS = [
  { value: 'services', label: 'Service based business' },
  { value: 'products', label: 'Products based business' },
];

export const BUSINESS_KIND_VALUES = BUSINESS_KIND_OPTIONS.map((o) => o.value);

/** @typedef {'online_center'|'education'|'service_general'|'product_retail'} FeatureTemplate */

/** Legacy KNLS / franchise types (unchanged DB values). */
export const LEGACY_FRANCHISE_BUSINESS_TYPES = [
  { value: 'online_center', label: 'Online Center', group: 'services', template: 'online_center' },
  {
    value: 'computer_training_center',
    label: 'Computer Training Center',
    group: 'services',
    template: 'education',
  },
  { value: 'coaching_center', label: 'Coaching Center', group: 'services', template: 'education' },
  { value: 'college', label: 'College', group: 'services', template: 'education' },
];

/**
 * Main categories + subcategories (mirrors apps/locsy_businessmitra category_service.dart).
 * defaultGroup: default bucket when sub is not in serviceSubs/productSubs.
 * serviceSubs / productSubs: override per subcategory id.
 */
const MITRA_CATEGORY_TREE = [
  {
    id: 'general_daily_shops',
    defaultGroup: 'products',
    serviceSubs: [
      'electrical_repair',
      'kitchen_appliances',
      'gas_refilling',
      'water_delivery',
      'pest_control',
    ],
    subs: [
      ['kirana', 'Kirana / General Stores'],
      ['provision', 'Provision Stores'],
      ['fancy', 'Fancy Shops'],
      ['stationery', 'Stationery / Office Supplies'],
      ['books', 'Book Stores / Depots'],
      ['gifts', 'Gift Shops / Toy Shops'],
      ['cosmetics', 'Cosmetic Shops'],
      ['baby_care', 'Baby Care Shops'],
      ['hardware', 'Hardware Shops'],
      ['paints', 'Paints / Adhesives / Industrial'],
      ['kitchenware', 'Kitchenware / Utensils'],
      ['furniture', 'Furniture Shops'],
      ['scrap', 'Scrap Buyers / Old Material'],
      ['kitchen_appliances', 'Kitchen Appliances Sales & Service'],
      ['electrical_repair', 'Electrical Appliance Repair'],
      ['gas_refilling', 'Gas Refilling / Gas Cylinder'],
      ['water_delivery', 'Water Delivery / Mineral Water'],
      ['flowers', 'Flower Shops / Florists'],
      ['pest_control', 'Pest Control Services'],
    ],
  },
  {
    id: 'home_needs',
    defaultGroup: 'products',
    serviceSubs: ['home_appliance_service', 'gas_stove_service'],
    subs: [
      ['cookers', 'Cookers / Pressure Cookers'],
      ['gas_stoves', 'Gas Stoves / Gas Cuts'],
      ['mixies', 'Mixies / Grinders'],
      ['fans', 'Fans (Ceiling / Table / Exhaust)'],
      ['irons', 'Iron Boxes'],
      ['water_heaters', 'Water Heaters / Geysers'],
      ['home_appliance_service', 'Home Appliance Sales & Service'],
      ['gas_stove_service', 'Gas Stove Repair / Service'],
    ],
  },
  {
    id: 'cloth_fashion',
    defaultGroup: 'products',
    serviceSubs: ['tailors', 'boutiques', 'blouse_stitching', 'embroidery', 'jewelry_polishing'],
    subs: [
      ['cloth_shops', 'Cloth Shops (Sarees, Dress Material)'],
      ['readymade', 'Readymade Garment Shops'],
      ['tailors', 'Tailors (Men/Women)'],
      ['boutiques', 'Boutiques & Fashion Designers'],
      ['blouse_stitching', 'Blouse Stitching Centres'],
      ['embroidery', 'Embroidery / Fall-Pico Centres'],
      ['footwear', 'Footwear Shops & Shoe Repair'],
      ['gold_jewelry', 'Gold Jewelry Shops'],
      ['silver_jewelry', 'Silver Jewelry Shops'],
      ['fancy_jewelry', 'Fancy / Imitation Jewelry'],
      ['jewelry_polishing', 'Jewelry Polishing / Merugu Petti'],
      ['silver_articles', 'Silver Articles (Pooja Items)'],
      ['watches', 'Watch Shops & Repair'],
    ],
  },
  {
    id: 'food_hotels',
    defaultGroup: 'products',
    serviceSubs: ['catering'],
    subs: [
      ['tiffin', 'Tiffin Centres'],
      ['curry_points', 'Curry Points / Mess / Bhojanamulu'],
      ['restaurants', 'Restaurants'],
      ['hotels', 'Hotels & Lodges'],
      ['tea_stalls', 'Tea Stalls'],
      ['juice_shops', 'Juice Shops'],
      ['ice_cream', 'Ice Cream Parlours'],
      ['chat_bandis', 'Chat Bandis / Street Food'],
      ['bakeries', 'Bakeries'],
      ['sweet_shops', 'Sweet Shops'],
      ['catering', 'Catering Services'],
    ],
  },
  {
    id: 'daily_essentials',
    defaultGroup: 'products',
    subs: [
      ['vegetables', 'Vegetable Shops'],
      ['leafy_vegetables', 'Leafy Vegetable Shops'],
      ['fruits', 'Fruit Shops'],
      ['meat_shops', 'Meat Shops (Chicken, Mutton, Fish)'],
      ['dairy', 'Dairy / Milk Centres'],
      ['halwa', 'Halwa / Sweet Centres'],
      ['dry_fruits', 'Dry Fruits / Spices Shops'],
    ],
  },
  {
    id: 'automobiles',
    defaultGroup: 'products',
    serviceSubs: [
      'car_service',
      'car_denting',
      'car_wash',
      'bike_service',
      'tyre_puncture',
      'auto_repair',
      'welding',
      'taxi_booking',
    ],
    subs: [
      ['car_showrooms', 'Car Showrooms'],
      ['car_service', 'Car Service Centres'],
      ['car_accessories', 'Car Accessories & Audio'],
      ['car_battery', 'Car Battery Shops'],
      ['car_denting', 'Car Denting / Painting / Body Works'],
      ['car_wash', 'Car Wash / Detailing'],
      ['taxi_booking', 'Taxi / Car Booking Offices'],
      ['bike_showrooms', 'Bike Showrooms'],
      ['bike_finance', 'Bike Finance Shops'],
      ['second_hand_bikes', 'Second-hand Bike Dealers'],
      ['bike_service', 'Bike Service Centres / Garages'],
      ['bike_spares', 'Bike Spare Parts Shops'],
      ['tyre_shops', 'Tyre Shops (New & Used)'],
      ['tyre_puncture', 'Tyre Puncture Shops'],
      ['seat_covers', 'Seat Cover & Upholstery'],
      ['auto_repair', 'Auto Rickshaw Repair / Spares'],
      ['goods_auto', 'Goods Auto Services'],
      ['vehicle_oil', 'Vehicle Oil / Lubricant'],
      ['vehicle_water', 'Vehicle Water Service'],
      ['welding', 'Welding / Fabrication'],
    ],
  },
  {
    id: 'health_medical',
    defaultGroup: 'services',
    productSubs: ['medical_shops', 'medical_equipment', 'ayurvedic'],
    subs: [
      ['hospitals', 'Hospitals'],
      ['clinics', 'Clinics (General & Specialist)'],
      ['diagnostic_labs', 'Diagnostic Labs'],
      ['xray', 'X-Ray Centres'],
      ['scan', 'Scan Centres'],
      ['blood_banks', 'Blood Banks / Blood Donation'],
      ['medical_shops', 'Medical Shops (Pharmacies)'],
      ['medical_equipment', 'Medical Equipment Shops'],
      ['ayurvedic', 'Ayurvedic / Homeo / Siddha'],
      ['skin_care', 'Skin Care Clinics / Spas'],
    ],
  },
  {
    id: 'local_services',
    defaultGroup: 'services',
    productSubs: ['surgical_shop', 'optical_shop', 'pet_shop', 'cycle_shop', 'packaging_shop'],
    subs: [
      ['barbershops', 'Barbershops / Salons'],
      ['beauty_parlours', 'Beauty Parlours'],
      ['laundry', 'Laundry / Ironing / Dry Cleaners'],
      ['electricians', 'Electricians'],
      ['plumbers', 'Plumbers'],
      ['carpenters', 'Carpenters'],
      ['painters', 'Painters'],
      ['house_cleaning', 'House Cleaning / Septic Tank'],
      ['mobile_repair', 'Mobile Repair Shops'],
      ['computer_repair', 'Computer Repair Shops'],
      ['internet', 'Internet / Net Centres'],
      ['xerox', 'Xerox / Printing / Lamination'],
      ['photo_studios', 'Photo Studios'],
      ['sign_boards', 'Sign Boards / Printing Press'],
      ['flex_printers', 'Flex Printers / Banner Printing'],
      ['banner_hoarding', 'Banner / Hoarding Printing'],
      ['borewell', 'Borewell Drilling Services'],
      ['water_purifier', 'Water Purifier Sales & Service'],
      ['cctv', 'CCTV Installation & Service'],
      ['ac_sales_service', 'AC Sales & Service'],
      ['optical_shop', 'Optical Shop / Eye Testing'],
      ['pet_shop', 'Pet Shop & Pet Care'],
      ['cycle_shop', 'Cycle / Bicycle Shop'],
      ['dental_clinic', 'Dental Clinic'],
      ['physiotherapy', 'Physiotherapy Center'],
      ['cold_storage', 'Cold Storage / Ice Factory'],
      ['packaging_shop', 'Packaging Material Shop'],
      ['surgical_shop', 'Surgical / Medical Supplies Shop'],
    ],
  },
  {
    id: 'media_communication',
    defaultGroup: 'services',
    subs: [
      ['newspaper', 'Newspaper Agents / Paper Distributors'],
      ['magazines', 'Magazine Distributors'],
      ['cable_tv', 'Cable TV Operators'],
      ['dth', 'DTH Providers (Tata Sky, Dish TV)'],
      ['broadband', 'Broadband / WiFi Providers'],
      ['local_isps', 'Local Internet Service Providers'],
      ['mobile_sim', 'Mobile SIM / Recharge Shops'],
    ],
  },
  {
    id: 'education_learning',
    defaultGroup: 'services',
    productSubs: ['school_uniforms', 'book_stationery'],
    subs: [
      ['schools', 'Schools'],
      ['junior_colleges', 'Junior Colleges'],
      ['degree_colleges', 'Degree Colleges'],
      ['coaching', 'Coaching Centres'],
      ['tuition', 'Tuition Centres'],
      ['computer_training', 'Computer Training Institutes'],
      ['local_tutors', 'Local Tutors / Trainers (Learn Zone)'],
      ['school_uniforms', 'School Uniform Shops'],
      ['book_stationery', 'Book & Stationery Shops'],
    ],
  },
  {
    id: 'events_lifestyle',
    defaultGroup: 'services',
    subs: [
      ['function_halls', 'Function Halls / Banquet Venues'],
      ['event_managers', 'Event Managers'],
      ['decorators', 'Decorators'],
      ['photographers', 'Photographers & Videographers'],
      ['sound_dj', 'Sound & DJ Rentals'],
      ['tent_house', 'Tent House / Event Equipment'],
    ],
  },
  {
    id: 'religion_community',
    defaultGroup: 'services',
    subs: [
      ['temples', 'Temples'],
      ['churches', 'Churches'],
      ['mosques', 'Mosques'],
      ['community_halls', 'Community Halls'],
      ['religious_events', 'Religious Events (Utsavams, Fairs)'],
      ['orphanages', 'Orphanages'],
      ['old_age_homes', 'Old Age Homes'],
      ['charity_clubs', 'Local Charity Clubs'],
      ['pg_hostels', 'PG / Hostels'],
      ['kapu_sangham', 'Kapu Sangham'],
      ['kamma_sangham', 'Kamma Sangham'],
      ['chowdary_sangham', 'Chowdary Sangham'],
      ['telaga_sangham', 'Telaga Sangham'],
      ['arya_vysya', 'Arya Vysya Sangham'],
      ['brahmana_sangham', 'Brahmana Sangham'],
      ['padmashali_sangham', 'Padmashali Sangham'],
      ['reddy_sangham', 'Reddy Sangham'],
      ['yadav_sangham', 'Yadav Sangham'],
      ['sc_st_sanghams', 'SC / ST Sanghams'],
      ['muslim_associations', 'Muslim Associations'],
      ['christian_associations', 'Christian Associations'],
      ['other_sanghams', 'Other Local Sanghams'],
    ],
  },
  {
    id: 'real_estate',
    defaultGroup: 'services',
    subs: [
      ['commercial_sites', 'Commercial Sites'],
      ['shops_sale', 'Shops for Sale'],
      ['office_spaces', 'Office Spaces'],
      ['godowns', 'Godowns / Warehouses'],
      ['paddy_lands', 'Paddy Lands'],
      ['palm_oil', 'Palm Oil Plantations'],
      ['coconut', 'Coconut Gardens'],
      ['banana', 'Banana Fields'],
      ['mango', 'Mango Orchards'],
      ['cashew', 'Cashew / Other Plantations'],
      ['house_sites', 'House Sites'],
      ['plots', 'Plots (DTCP / Open)'],
      ['independent_houses', 'Independent Houses'],
      ['duplex', 'Duplex Houses'],
      ['villas', 'Villas'],
      ['apartments', 'Apartment Flats (1BHK, 2BHK, 3BHK)'],
      ['houses_rent', 'Houses for Rent'],
      ['shops_rent', 'Shops for Rent'],
      ['apartments_rent', 'Apartments / Flats for Rent'],
      ['pg_hostels_rent', 'PG / Hostels'],
      ['godowns_rent', 'Godowns / Storage for Rent'],
    ],
  },
  {
    id: 'resale_marketplace',
    defaultGroup: 'products',
    subs: [
      ['second_hand_furniture', 'Second-hand Furniture'],
      ['used_electronics', 'Used Electronics (TV, Fridge, AC)'],
      ['used_vehicles', 'Used Vehicles (Bikes, Cars, Autos)'],
      ['used_books', 'Used Books / Uniforms'],
      ['used_agricultural', 'Used Agricultural Tools / Machinery'],
      ['scrap_buyers', 'Scrap Buyers (Iron, Plastic, Paper)'],
    ],
  },
  {
    id: 'business_finance_legal',
    defaultGroup: 'services',
    subs: [
      ['banks', 'Banks / ATMs'],
      ['insurance', 'Insurance Agents'],
      ['loan_finance', 'Loan / Finance Services'],
      ['ca_tax', 'Chartered Accountants (CA) / Tax'],
      ['document_writers', 'Document Writers'],
      ['advocates', 'Advocates / Notaries'],
      ['rto_agents', 'RTO Agents'],
    ],
  },
  {
    id: 'transport_workers',
    defaultGroup: 'services',
    subs: [
      ['rickshaw_pullers', 'Rickshaw Pullers'],
      ['passenger_autos', 'Passenger Autos (7-seater / 10-seater)'],
      ['goods_auto', 'Goods Auto Services'],
      ['goods_carriers', 'Goods Carriers (Lorries, Mini Trucks)'],
      ['loaders', 'Loaders / Coolies'],
      ['parcel_courier', 'Parcel & Courier Services'],
    ],
  },
  {
    id: 'construction_skilled_labour',
    defaultGroup: 'services',
    subs: [
      ['masons', 'Masons'],
      ['helpers', 'Helpers (General Labour)'],
      ['rod_benders', 'Rod Benders / Steel Fixers'],
      ['tile_layers', 'Tile Layers / Marble Workers'],
      ['pop_workers', 'POP / Gypsum / False Ceiling'],
      ['vadra_workers', 'Vadra Workers (Mud / Brick Work)'],
      ['painters', 'Painters'],
      ['carpenters', 'Carpenters'],
      ['welders', 'Welders / Fabricators'],
      ['plumbers', 'Plumbers'],
      ['electricians', 'Electricians'],
    ],
  },
  {
    id: 'fitness_sports',
    defaultGroup: 'services',
    subs: [
      ['gyms', 'Gyms / Fitness Centres'],
      ['yoga', 'Yoga Classes'],
      ['dance', 'Dance Classes'],
      ['sports_coaching', 'Sports Coaching Centres'],
      ['playgrounds', 'Playgrounds / Indoor Sports'],
    ],
  },
  {
    id: 'street_vendors',
    defaultGroup: 'products',
    subs: [
      ['vegetables_cart', 'Vegetable Cart'],
      ['fruits_cart', 'Fruit Cart'],
      ['street_food_cart', 'Street Food / Chat Cart'],
      ['flowers_cart', 'Flower Cart'],
      ['household_cart', 'Household / Utensils Cart'],
      ['other_cart', 'Other Mobile Vendor'],
    ],
  },
];

const resolveSubGroup = (category, subId) => {
  const serviceSet = new Set(category.serviceSubs || []);
  const productSet = new Set(category.productSubs || []);
  if (serviceSet.has(subId)) return 'services';
  if (productSet.has(subId)) return 'products';
  return category.defaultGroup;
};

const resolveTemplate = (group, mainId) => {
  if (mainId === 'education_learning') return 'education';
  if (group === 'products') return 'product_retail';
  return 'service_general';
};

export const buildMitraBusinessTypeValue = (mainId, subId) => `${mainId}__${subId}`;

const buildMitraTypes = () => {
  const out = [];
  for (const category of MITRA_CATEGORY_TREE) {
    for (const [subId, label] of category.subs) {
      const group = resolveSubGroup(category, subId);
      out.push({
        value: buildMitraBusinessTypeValue(category.id, subId),
        label,
        group,
        template: resolveTemplate(group, category.id),
        mainCategoryId: category.id,
        subCategoryId: subId,
      });
    }
  }
  return out;
};

/** All selectable types: legacy + Mitra subcategories (dedupe legacy labels already in tree). */
export const FRANCHISEE_BUSINESS_TYPES = [
  ...LEGACY_FRANCHISE_BUSINESS_TYPES,
  ...buildMitraTypes(),
];

export const BUSINESS_TYPE_VALUES = FRANCHISEE_BUSINESS_TYPES.map((t) => t.value);

export const BUSINESS_TYPE_BY_VALUE = Object.fromEntries(
  FRANCHISEE_BUSINESS_TYPES.map((t) => [t.value, t]),
);

export const getBusinessTypesByGroup = () => {
  const services = FRANCHISEE_BUSINESS_TYPES.filter((t) => t.group === 'services');
  const products = FRANCHISEE_BUSINESS_TYPES.filter((t) => t.group === 'products');
  return { services, products };
};

export const getBusinessTypesForKind = (kind) => {
  const { services, products } = getBusinessTypesByGroup();
  if (kind === 'services') return services;
  if (kind === 'products') return products;
  return [];
};

/** Filter for category search: all | services | products */
export const getBusinessTypesForFilter = (filter) => {
  if (filter === 'services' || filter === 'products') {
    return getBusinessTypesForKind(filter);
  }
  return FRANCHISEE_BUSINESS_TYPES;
};

export const getBusinessTypeLabel = (value) =>
  BUSINESS_TYPE_BY_VALUE[value]?.label ||
  String(value || '')
    .split('__')
    .pop()
    ?.replace(/_/g, ' ') ||
  value;

export const getBusinessTypeGroup = (value) =>
  BUSINESS_TYPE_BY_VALUE[value]?.group || 'services';
